import { useState, useEffect } from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, LabelList, Cell,
} from "recharts";
import { Loader2, Sparkles } from "lucide-react";
import { useNovexData } from "@/hooks/useNovexData";
import { useLang } from "@/context/LanguageContext";
import { MarkdownLite } from "@/components/MarkdownLite";
import { SourceBadge } from "@/components/SourceBadge";
import { TreasuryCurve } from "@/components/TreasuryCurve";

const pct = (bps) => (bps == null ? "—" : `${(bps / 100).toFixed(2)}%`);
const baseCfg = (t) => ({
  cut: { label: t("ird.baseCut"), color: "#10B981" },
  hold: { label: t("ird.baseHold"), color: "#64748B" },
  hike: { label: t("ird.baseHike"), color: "#EF4444" },
});

function useNow(active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

function countdown(dateIso, now, t) {
  if (!dateIso) return "—";
  const ts = new Date(dateIso).getTime();
  if (isNaN(ts)) return "—";
  const ms = ts - now;
  if (ms <= 0) return t("ird.inProgress");
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 86400)}j ${Math.floor((s % 86400) / 3600)}h ${Math.floor((s % 3600) / 60)}m ${s % 60}s`;
}

function moveBps(m) {
  if (m.expected_move_bps != null) return m.expected_move_bps;
  if (m.expected_rate_bps != null && m.current_target_mid_bps != null) return m.expected_rate_bps - m.current_target_mid_bps;
  return null;
}

function ProbBar({ label, value, color }) {
  const v = value == null ? null : Math.round(value * 10) / 10;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <span className="font-mono font-bold" style={{ color: v == null ? "#475569" : color }}>{v == null ? "—" : `${v}%`}</span>
      </div>
      <div className="h-6 w-full overflow-hidden rounded bg-[#161A22]">
        {v != null && <div className="h-full rounded transition-all" style={{ width: `${Math.max(2, v)}%`, background: color }} />}
      </div>
    </div>
  );
}

function NextMove({ m, fallback, t }) {
  const BASE = baseCfg(t);
  const bc = BASE[m.base_case] || { label: "—", color: "#64748B" };
  const mv = moveBps(m);
  const range = (m.current_target_lower_bps != null && m.current_target_upper_bps != null)
    ? `${pct(m.current_target_lower_bps)}–${pct(m.current_target_upper_bps)}` : pct(m.current_target_mid_bps);
  return (
    <div className="rounded-lg border border-[#222733] bg-[#0E1118] p-4 sm:p-5" data-testid="rates-next-move">
      <h3 className="mb-3 font-heading text-xs font-bold uppercase tracking-wider text-slate-400">{t("ird.nextMove")}</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("ird.baseScenario")}</div>
          <div className="mt-1 text-lg font-bold" style={{ color: bc.color }}>{bc.label}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("ird.expected")}</div>
          <div className="mt-1 font-mono text-lg font-bold" style={{ color: mv == null ? "#94A3B8" : mv > 0 ? "#EF4444" : mv < 0 ? "#10B981" : "#94A3B8" }}>
            {mv == null ? "—" : `${mv > 0 ? "+" : ""}${(mv).toFixed(1)} bps`}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("ird.currentRate")}</div>
          <div className="mt-1 font-mono text-lg font-bold text-white">{range}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("ird.meetingDate")}</div>
          <div className="mt-1 font-mono text-lg font-bold text-white">{m.meeting_label || "—"}</div>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        <ProbBar label={t("ird.cut")} value={m.cut_probability} color="#10B981" />
        <ProbBar label={t("ird.hold")} value={m.hold_probability} color="#64748B" />
        <ProbBar label={t("ird.hike")} value={m.hike_probability} color="#EF4444" />
      </div>
      {fallback && m.hold_probability == null && (
        <p className="mt-3 text-[11px] text-amber-500/80">
          {t("ird.fallbackNote")}
        </p>
      )}
    </div>
  );
}

function ForwardCurve({ meetings, t }) {
  const data = meetings.map((m) => ({
    label: m.meeting_label,
    now: m.expected_rate_bps != null ? m.expected_rate_bps / 100 : null,
    w1: m.expected_rate_one_week_ago_bps != null ? m.expected_rate_one_week_ago_bps / 100 : null,
    m1: m.expected_rate_one_month_ago_bps != null ? m.expected_rate_one_month_ago_bps / 100 : null,
  }));
  const hasW1 = data.some((d) => d.w1 != null);
  const hasM1 = data.some((d) => d.m1 != null);
  return (
    <div className="rounded-lg border border-[#222733] bg-[#0E1118] p-4 sm:p-5" data-testid="rates-forward-curve">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-slate-400">{t("ird.fwdCurve")}</h3>
        <div className="flex gap-3 text-[10px]">
          <span className="flex items-center gap-1 text-white"><span className="h-1.5 w-3 rounded bg-white" />{t("ird.current")}</span>
          {hasW1 && <span className="flex items-center gap-1 text-red-300"><span className="h-1.5 w-3 rounded bg-red-400" />{t("ird.w1")}</span>}
          {hasM1 && <span className="flex items-center gap-1 text-emerald-300"><span className="h-1.5 w-3 rounded bg-emerald-400" />{t("ird.m1")}</span>}
        </div>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="99%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: -18 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#475569" }} axisLine={false} tickLine={false} minTickGap={20} />
            <YAxis tick={{ fontSize: 10, fill: "#475569" }} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} width={44} domain={["auto", "auto"]} />
            <Tooltip contentStyle={{ background: "#0B0E14", border: "1px solid #222733", borderRadius: 8, fontSize: 12 }} formatter={(v, n) => [`${Number(v).toFixed(2)}%`, { now: t("ird.current"), w1: t("ird.w1"), m1: t("ird.m1") }[n] || n]} />
            {hasM1 && <Line type="monotone" dataKey="m1" stroke="#34D399" strokeWidth={1.6} dot={false} isAnimationActive={false} connectNulls />}
            {hasW1 && <Line type="monotone" dataKey="w1" stroke="#F87171" strokeWidth={1.6} dot={false} isAnimationActive={false} connectNulls />}
            <Line type="monotone" dataKey="now" stroke="#FFFFFF" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ForwardBars({ meetings, t }) {
  const data = meetings.map((m) => ({ label: m.meeting_label, rate: m.expected_rate_bps != null ? Math.round(m.expected_rate_bps) / 100 : null }));
  return (
    <div className="rounded-lg border border-[#222733] bg-[#0E1118] p-4 sm:p-5" data-testid="rates-forward-bars">
      <h3 className="mb-3 font-heading text-xs font-bold uppercase tracking-wider text-slate-400">{t("ird.fwdByMeeting")}</h3>
      <div className="h-56">
        <ResponsiveContainer width="99%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 5, bottom: 0, left: -18 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#475569" }} axisLine={false} tickLine={false} minTickGap={10} />
            <YAxis tick={{ fontSize: 10, fill: "#475569" }} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} width={44} domain={["auto", "auto"]} />
            <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#0B0E14", border: "1px solid #222733", borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${Number(v).toFixed(2)}%`, t("ird.fwdRate")]} />
            <Bar dataKey="rate" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {data.map((_, i) => <Cell key={i} fill="#10B981" />)}
              <LabelList dataKey="rate" position="top" formatter={(v) => (v == null ? "" : `${v.toFixed(2)}%`)} style={{ fontSize: 10, fill: "#94A3B8" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function FutureMeetings({ meetings, now, t }) {
  const BASE = baseCfg(t);
  return (
    <div className="rounded-lg border border-[#222733] bg-[#0E1118] p-4 sm:p-5" data-testid="rates-future-meetings">
      <h3 className="mb-3 font-heading text-xs font-bold uppercase tracking-wider text-slate-400">{t("ird.futureMeetings")}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#222733] text-[10px] uppercase tracking-wider text-slate-500">
              <th className="py-2 pr-3 text-left">{t("ird.colDate")}</th>
              <th className="py-2 pr-3 text-left">{t("ird.colCountdown")}</th>
              <th className="py-2 pr-3 text-right">{t("ird.cut")}</th>
              <th className="py-2 pr-3 text-right">{t("ird.hold")}</th>
              <th className="py-2 pr-3 text-right">{t("ird.hike")}</th>
              <th className="py-2 pr-3 text-right">{t("ird.expected")}</th>
              <th className="py-2 text-right">{t("ird.colScenario")}</th>
            </tr>
          </thead>
          <tbody>
            {meetings.map((m, i) => {
              const bc = BASE[m.base_case];
              const mv = moveBps(m);
              return (
                <tr key={i} className="border-b border-[#222733] last:border-0 hover:bg-[#161A22]" data-testid={`rate-meeting-${i}`}>
                  <td className="py-2.5 pr-3 font-mono font-semibold text-slate-200">{m.meeting_label}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-slate-400">{countdown(m.meeting_date, now, t)}</td>
                  <td className="py-2.5 pr-3 text-right font-mono text-[#10B981]">{m.cut_probability == null ? "—" : `${Math.round(m.cut_probability)}%`}</td>
                  <td className="py-2.5 pr-3 text-right font-mono text-slate-300">{m.hold_probability == null ? "—" : `${Math.round(m.hold_probability)}%`}</td>
                  <td className="py-2.5 pr-3 text-right font-mono text-[#EF4444]">{m.hike_probability == null ? "—" : `${Math.round(m.hike_probability)}%`}</td>
                  <td className="py-2.5 pr-3 text-right font-mono text-slate-300">{mv == null ? "—" : `${mv > 0 ? "+" : ""}${mv.toFixed(1)} bps`}</td>
                  <td className="py-2.5 text-right">
                    {bc ? <span className="font-bold" style={{ color: bc.color }}>{bc.label}</span> : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function InterestRatesDetail() {
  const { t, lang } = useLang();
  const rates = useNovexData("/novex/interest-rates?bank=fed");
  const ai = useNovexData(`/ai/rate-scenarios?bank=fed&lang=${lang}`, { maxPolls: 20 });
  const meetings = Array.isArray(rates.data?.meetings) ? rates.data.meetings : [];
  const fallback = !!rates.data?.fallback;
  const now = useNow(meetings.length > 0);

  return (
    <div className="space-y-4" data-testid="interest-rates-detail">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white" data-testid="bank-fed">🇺🇸 {t("ir.fedName")}</span>
        <SourceBadge source={rates.source} />
        {rates.data?.implied_source && (
          <span className="rounded bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-300" data-testid="rates-implied-source">
            {t("ir.impliedMkt", { src: rates.data.implied_source })}
          </span>
        )}
      </div>

      {rates.status === "loading" ? (
        <div className="flex items-center gap-2 py-8 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> {t("ir.loadingExp")}</div>
      ) : !meetings.length ? (
        <p className="text-sm text-slate-500">{t("ird.noRates")}</p>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <NextMove m={meetings[0]} fallback={fallback} t={t} />
            <ForwardCurve meetings={meetings} t={t} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <FutureMeetings meetings={meetings} now={now} t={t} />
            <ForwardBars meetings={meetings} t={t} />
          </div>

          <TreasuryCurve />

          <div className="rounded-lg border border-[#222733] bg-[#0E1118] p-4 sm:p-5">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-blue-300">
              <Sparkles className="h-3.5 w-3.5" /> {t("ir.aiTitle")}
            </div>
            {ai.status === "loading" || ai.status === "generating" ? (
              <div className="flex items-center gap-2 py-4 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> {t("ir.genScenarios")}</div>
            ) : ai.data?.analysis ? (
              <div className="text-sm text-slate-300"><MarkdownLite text={ai.data.analysis} /></div>
            ) : (
              <p className="text-sm text-slate-500">{t("ir.analysisNone")}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
